import type { Order, SortBy } from '../types';

import { STATUS_PRIORITY, laneFor } from './status';

/**
 * Left lane (Preparing): status priority descending, time ascending — new orders land at the bottom.
 * Right lane (Ready/Collected): status priority ascending, time descending — newest orders at the top.
 *
 * When `sortBy === 'priorityScore'`, orders with a non-null `priorityScore`
 * are ordered highest-score-first within their status group (§2.4); orders
 * without a score fall back to the timestamp tiebreakers below.
 */
export function sortOrders(orders: ReadonlyArray<Order>, sortBy: SortBy | null = null): Order[] {
  return orders.slice().sort((a, b) => compare(a, b, sortBy));
}

function compare(a: Order, b: Order, sortBy: SortBy | null): number {
  const pa = STATUS_PRIORITY[a.status];
  const pb = STATUS_PRIORITY[b.status];
  // Left-lane orders sort status priority in reverse (in_progress first, pending last).
  const dir = laneFor(a.status) === 'left' ? -1 : 1;
  if (pa !== pb) return dir * (pa - pb);

  if (sortBy === 'priorityScore') {
    // Higher score first; missing scores sort last and defer to the timestamp keys.
    const sa = priority(a.priorityScore);
    const sb = priority(b.priorityScore);
    if (sa !== sb) return sb - sa;
  }

  const ub = timestamp(b.updatedAt);
  const ua = timestamp(a.updatedAt);
  if (ub !== ua) return dir * (ub - ua);

  const cb = timestamp(b.createdAt);
  const ca = timestamp(a.createdAt);
  if (cb !== ca) return dir * (cb - ca);

  if (a.code < b.code) return -1;
  if (a.code > b.code) return 1;
  return 0;
}

/**
 * Parses an ISO timestamp to a numeric value for comparison. Missing or
 * unparseable timestamps sort as -Infinity so they end up *last* in
 * descending order, which is the behaviour we want.
 */
function timestamp(iso: string | null): number {
  if (!iso) return -Infinity;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : -Infinity;
}

/**
 * Maps a priority score to a comparable number. Null / missing scores become
 * -Infinity so that scored orders always sort ahead of unscored ones.
 */
function priority(score: number | null): number {
  return typeof score === 'number' && Number.isFinite(score) ? score : -Infinity;
}
